import { useTranslation } from "react-i18next";
import { useIntl } from "react-intl";
import { convertBpToDecimal, formatNumberLocale } from "@/utils/helpers";
import { TextInfoGroupAlt } from "@/components/shared";
import { Service, ServiceType, ShiftType } from "@/graphql/__generated__/graphql";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import Decimal from "decimal.js";

interface TotalOverviewPricingProps {
  service: Service;
}

function TotalOverviewPricing({ service }: TotalOverviewPricingProps) {
  const { t } = useTranslation();
  const intl = useIntl();

  const cumulatedBasePricing = service.pricing.servicePricing.find(
    (pricing) => pricing.type === ShiftType.Cumulated
  )!;
  const cumulatedCarPurchasePricing = service.pricing.carPurchasePricing.find(
    (pricing) => pricing.type === ShiftType.Cumulated
  )!;

  const basePricePerHour = cumulatedBasePricing.pricePerHour;
  const baseRevenue = cumulatedBasePricing.revenue;

  const carPricePerHour = cumulatedCarPurchasePricing.carPricePerHour;
  const purchasePricePerHour = cumulatedCarPurchasePricing.purchasePricePerHour;

  const carRevenue = cumulatedCarPurchasePricing.carRevenue;
  const purchaseRevenue = cumulatedCarPurchasePricing.purchaseRevenue;

  const serviceOverviewMetrics = [
    {
      label: t("common:pricing.labels.totalsurcharge"),
      value: `${convertBpToDecimal(service.pricing?.totalSurcharge ?? 0)}%`,
      tooltip: t("common:pricing.tooltips.totalsurcharge"),
    },
    {
      label: t("common:pricing.labels.totalrevenue"),
      value: formatNumberLocale(intl, service.pricing?.totalRevenue ?? 0, "currency"),
      tooltip: t("common:pricing.tooltips.totalrevenue"),
      breakdown: {
        base: formatNumberLocale(intl, baseRevenue, "currency"),
        car: formatNumberLocale(intl, carRevenue, "currency"),
        purchase: formatNumberLocale(intl, purchaseRevenue, "currency"),
      },
    },
    {
      label: t("common:pricing.labels.totalpriceperhour"),
      value: formatNumberLocale(intl, service.pricing?.totalPricePerHour ?? 0, "currency"),
      tooltip: t("common:pricing.tooltips.totalpriceperhour"),
      breakdown: {
        base: formatNumberLocale(intl, basePricePerHour, "currency"),
        car: formatNumberLocale(intl, carPricePerHour, "currency"),
        purchase: formatNumberLocale(intl, purchasePricePerHour, "currency"),
      },
    },
    {
      label: t("common:pricing.labels.totalyearlyrate"),
      value: formatNumberLocale(intl, service.pricing?.yearlyRate ?? 0, "currency"),
      hasCustomSchedule: service.pricing?.hasCustomSchedule,
      tooltip: t("common:pricing.tooltips.totalyearlyrate"),
      unavailableTooltip: t("components:pricedetails.tooltips.yearlyrateunavailable"),
    },
    {
      label: t("common:pricing.labels.totalmonthlyrate"),
      value: formatNumberLocale(intl, service.pricing?.monthlyRate ?? 0, "currency"),
      hasCustomSchedule: service.pricing?.hasCustomSchedule,
      tooltip: t("common:pricing.tooltips.totalmonthlyrate"),
      unavailableTooltip: t("components:pricedetails.tooltips.monthlyrateunavailable"),
    },
    ...(service.type === ServiceType.Mobile ? [
      {
        label: t("common:pricing.labels.totalpriceperminute"),
        value: formatNumberLocale(intl, new Decimal(service.pricing?.totalPricePerHour ?? 0).dividedBy(60).toNumber(), "currency"),
        tooltip: t("common:pricing.tooltips.totalpriceperminute"),
      },
    ] : []),
  ];

  return (
    <Card className="shadow-none">
      <CardHeader className="p-3">
        <CardTitle className="text-sm font-bold">
          {t("edittender.calculationoverview.labels.totaloverview")}
        </CardTitle>
      </CardHeader>
      <CardContent className="grid gap-3 px-3 pb-3" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))" }}>
        {serviceOverviewMetrics.map((metric) => {
          return (
            <div key={`metric-${metric.label}`} className="flex flex-col gap-1 rounded-md bg-slate-50 p-2">
              <TextInfoGroupAlt
                label={metric.label}
                content={metric.hasCustomSchedule ? t("components:pricedetails.unavailable") : metric.value}
                tooltip={metric.hasCustomSchedule ? metric.unavailableTooltip : metric.tooltip}
                labelClassName={metric.hasCustomSchedule ? "text-amber-500" : undefined}
              />
              {"breakdown" in metric && metric.breakdown ? (
                <div className="text-xs text-muted-foreground">
                  {`${metric.breakdown.base} + ${metric.breakdown.car} + ${metric.breakdown.purchase}`}
                </div>
              ) : null}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

export default TotalOverviewPricing;
